import {
    setClientDetailsQuery,
    setClientsQuery,
    useBusinessInfo,
    useClientDetailsQuery,
} from '../../lib/Queries';
import SidePanel from '../SidePanel.jsx';
import { formatDateLong, formatDateTime } from '../../lib/DateTime.js';
import FormAnswers from '../forms/submissions/FormAnswers';
import CalendarMarkIcon from '../icons/CalendarMarkIcon.jsx';
import PencilSquareIcon from '../icons/PencilSquareIcon';
import DotDotDotMenu from '../DotDotDotMenu';
import TrashIcon from '../icons/TrashIcon.jsx';
import SectionHeading from '../SectionHeading.jsx';
import BookingEventList from './BookingEventList.jsx';
import ClientFormSubmissionsList from './ClientFormSubmissionsList.jsx';
import { useEffect, useRef, useState } from 'react';
import TextInput from '../TextInput';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import ClientEditForm from './ClientEditForm.jsx';
import useDelete from '../../lib/useDelete.jsx';
import * as Toast from '../../lib/Toast.js';
import SubmissionDetailsPanel from '../forms/submissions/SubmissionDetailsPanel';
import EventDetailsPanel from '../calendar/EventDetailsPanel';
import SchedulingPanel from '../scheduling/SchedulingPanel.jsx';
import ClientPackageGrants from './ClientPackageGrants.jsx';
import CustomFields from '../customfields/CustomFields.jsx';
import { ObjectTypes } from '../../lib/ObjectTypes.js';
import { useDebounce } from '@uidotdev/usehooks';
import MergeClientsModal from '~/components/clients/MergeClientsModal.jsx';
import MergeIcon from '~/components/icons/MergeIcon.jsx';
import ClientSavedPaymentMethods from '~/components/clients/ClientSavedPaymentMethods';
import GiftCertificatesList from '~/components/giftcertificates/GiftCertificatesList';
import { XCircleIcon } from '@heroicons/react/24/outline';
import CircleBlockIcon from '~/components/icons/CircleBlockIcon';
import useBanClient, { BanBanner } from '~/lib/useBanClient';
import WaitlistEntry from '~/components/waitlist/WaitlistEntry.jsx';

const ClientDetails = ({ client, onClose, refetch }) => {
    const {
        isLoading,
        data,
        refetch: refetchClientDetails,
    } = useClientDetailsQuery(client?.id);
    const { save, isSaving } = useBusinessPostApi(`/clients/${client?.id}`);
    const { data: businessInfoData } = useBusinessInfo();

    const panelRef = useRef();

    const [newClientData, setNewClientData] = useState({});

    const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
    const [isMergeOpen, setIsMergeOpen] = useState(false);

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedSubmission, setSelectedSubmission] = useState(null);

    const showClient = data?.client || client;
    const ban = data?.ban;

    const { doBan, isBanning, banComponentSettings } = useBanClient({
        refetchClientDetails,
        ban,
        client,
    });

    const { saveDelete, isDeleting } = useDelete(
        `/clients/${client?.id}/delete`,
        {
            prompt: `Are you REALLY sure you want to delete ${showClient?.name}?\n\nThis is permanent and cannot be undone.`,
        },
    );

    function getClientData() {
        return {
            firstName: showClient?.first_name,
            lastName: showClient?.last_name,
            email: showClient?.email,
            phone: showClient?.phone,
        };
    }

    useEffect(() => {
        if (showClient) {
            setNewClientData(getClientData());
        }
    }, [JSON.stringify(showClient)]);

    const submissions = data?.submissions || [];
    const events = data?.events || [];
    const giftCertificates = data?.giftCertificates || [];

    function doSave() {
        save(newClientData).then(result => {
            //setClientDetailsQuery(client.id, result);
            refetch();
        });
    }

    const debouncedNewClientData = useDebounce(newClientData, 500);
    useEffect(() => {
        if (
            JSON.stringify(debouncedNewClientData) !==
                JSON.stringify(getClientData()) &&
            client?.id
        )
            doSave();
    }, [JSON.stringify(debouncedNewClientData)]);

    function doDelete() {
        if (
            prompt(
                `Are you sure you want to delete ${client.first_name} ${client.last_name}? This will delete all of their events, packages, and form submissions.\n\nType DELETE (in all caps) to confirm.`,
            ) === 'DELETE'
        ) {
            return saveDelete().then(result => {
                Toast.success(
                    `Successfully deleted ${client.first_name} ${client.last_name}`,
                );
                onClose();
                setClientsQuery(result);
            });
        }
    }

    useEffect(() => {
        // If we view a new client when editing, don't edit anymore
        //  otherwise old info might show
        panelRef.current?.closeAllAbove();
    }, [JSON.stringify(client?.id)]);

    return (
        <SidePanel
            ref={panelRef}
            onClose={onClose}
            isOpen={!!client}
            title="Client Details"
            //onSave={() => doSave()}
            saveLabel={isSaving ? 'Saving...' : 'Save Changes'}
        >
            {!!client && (
                <div className="">
                    <div className="flex items-center justify-between">
                        <h1 className="mr-2">
                            {newClientData.firstName} {newClientData.lastName}
                        </h1>

                        <div className="flex items-center justify-end">
                            <DotDotDotMenu
                                btnClassName="btn-light-circle"
                                options={[
                                    {
                                        name: 'Schedule',
                                        icon: <CalendarMarkIcon />,
                                        onClick: () =>
                                            setIsSchedulingOpen(true),
                                    },
                                    {
                                        name: 'Merge Clients',
                                        icon: <MergeIcon />,
                                        onClick: () => setIsMergeOpen(true),
                                    },
                                    {
                                        name: isBanning ? 'Banning...' : 'Ban',
                                        icon: <CircleBlockIcon />,
                                        onClick: doBan,
                                    },
                                    {
                                        name: 'Delete',
                                        isDelete: true,
                                        onClick: doDelete,
                                    },
                                ]}
                            />
                        </div>
                    </div>

                    <BanBanner banSettings={banComponentSettings} />

                    <MergeClientsModal
                        key={client.id}
                        isOpen={isMergeOpen}
                        onClose={() => setIsMergeOpen(false)}
                        onMergeSuccess={() => onClose()}
                        fromClient={client}
                    />

                    <ClientEditForm
                        newClientData={newClientData}
                        setNewClientData={setNewClientData}
                        isUnsubscribed={data?.unsubscribed}
                        client={showClient}
                        businessFeatures={businessInfoData?.features}
                        onResubscribe={() => {
                            refetchClientDetails();
                            refetch();
                        }}
                    />

                    <CustomFields
                        type={ObjectTypes.CLIENT}
                        id={client.id}
                        fields={client.custom_fields}
                        onChanged={() => {
                            refetchClientDetails();
                            refetch();
                        }}
                        className="mt-2 mb-4"
                    />

                    <BookingEventList
                        events={events}
                        onClick={event => {
                            setSelectedEvent(event);
                        }}
                        showBulkEdit={true}
                        refetch={refetchClientDetails}
                        beforeContent={
                            <>
                                {data?.waitlist?.length > 0 && (
                                    <div>
                                        <div className="panel-label mt-4">
                                            Waitlisting
                                        </div>
                                        <div className="panel pl-4">
                                            {data?.waitlist.map(
                                                (entry, idx) => (
                                                    <WaitlistEntry
                                                        key={entry.id}
                                                        entry={entry}
                                                        isFirst={idx === 0}
                                                        hideClient={true}
                                                    />
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        }
                    />

                    <ClientPackageGrants search={{ clientId: client.id }} />

                    {!!businessInfoData?.canPay && (
                        <ClientSavedPaymentMethods
                            client={showClient}
                            paymentMethods={showClient?.saved_payment_methods}
                            refetch={refetchClientDetails}
                            className="mt-4"
                        />
                    )}

                    {!!giftCertificates?.length && (
                        <GiftCertificatesList
                            giftCertificates={giftCertificates}
                            className="mt-4"
                            refetch={refetchClientDetails}
                        />
                    )}

                    <ClientFormSubmissionsList
                        submissions={submissions}
                        className="mt-8"
                        onClick={submission =>
                            setSelectedSubmission(submission)
                        }
                    />

                    <SubmissionDetailsPanel
                        onClose={() => setSelectedSubmission(null)}
                        isOpen={!!selectedSubmission}
                        submission={selectedSubmission}
                        refetch={refetchClientDetails}
                    />

                    <EventDetailsPanel
                        event={selectedEvent}
                        isOpen={!!selectedEvent}
                        onClose={() => setSelectedEvent(null)}
                        onEventChanged={() => refetchClientDetails()}
                    />

                    <SchedulingPanel
                        isOpen={isSchedulingOpen}
                        onClose={() => setIsSchedulingOpen(false)}
                        client={{
                            email: client.email,
                            firstName: client.first_name,
                            lastName: client.last_name,
                        }}
                        onScheduled={() => refetchClientDetails()}
                    />
                </div>
            )}
        </SidePanel>
    );
};

export default ClientDetails;
